import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import XeroRetrievalMonthCell from "@/components/admin/XeroRetrievalMonthCell";
import {
  XERO_DIM_CUBE_MAPPINGS_QUERY_KEY,
  DimCubeMapping,
  DimCubeMappingPayload,
  saveDimCubeMapping,
} from "@/services/xeroApi";


function formatUpdatedAt(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB");
}


export default function XeroMappingRow({
  mapping,
}: {
  mapping: DimCubeMapping;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [legacyOfficeDraft, setLegacyOfficeDraft] = useState(
    mapping.legacy_office ?? "",
  );
  const [legacyOfficeReportedDraft, setLegacyOfficeReportedDraft] = useState(
    mapping.legacy_office_reported ?? "",
  );
  const [siteDraft, setSiteDraft] = useState(mapping.site ?? "");
  const [legacyLocationReportedDraft, setLegacyLocationReportedDraft] = useState(
    mapping.legacy_location_reported ?? "",
  );
  const [mappingPerimeterDraft, setMappingPerimeterDraft] = useState(
    mapping.mapping_perimeter ?? "",
  );
  const [xeroInclusionDateDraft, setXeroInclusionDateDraft] = useState(
    mapping.xero_inclusion_date,
  );

  const nextLegacyOffice = legacyOfficeDraft.trim() || null;
  const nextLegacyOfficeReported = legacyOfficeReportedDraft.trim() || null;
  const nextSite = siteDraft.trim() || null;
  const nextLegacyLocationReported = legacyLocationReportedDraft.trim() || null;
  const nextMappingPerimeter = mappingPerimeterDraft.trim() || null;
  const isDirty =
    nextLegacyOffice !== mapping.legacy_office ||
    nextLegacyOfficeReported !== mapping.legacy_office_reported ||
    nextSite !== mapping.site ||
    nextLegacyLocationReported !== mapping.legacy_location_reported ||
    nextMappingPerimeter !== mapping.mapping_perimeter ||
    xeroInclusionDateDraft !== mapping.xero_inclusion_date;

  const canSave = mapping.row_id !== null;

  const save = useMutation({
    mutationFn: (payload: DimCubeMappingPayload) =>
      saveDimCubeMapping(mapping.row_id as string, payload),
    onSuccess: () => {
      toast({ title: "Mapping updated" });
      queryClient.invalidateQueries({ queryKey: XERO_DIM_CUBE_MAPPINGS_QUERY_KEY });
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: `Couldn’t update ${mapping.legacy_office_fetched}`,
        description: (e as Error).message,
      });
    },
  });

  const onSave = () => {
    if (!isDirty || save.isPending || !canSave) return;
    save.mutate({
      legacy_office: nextLegacyOffice,
      legacy_office_reported: nextLegacyOfficeReported,
      site: nextSite,
      legacy_location_reported: nextLegacyLocationReported,
      mapping_perimeter: nextMappingPerimeter,
      xero_inclusion_date: xeroInclusionDateDraft,
    });
  };

  const onCancel = () => {
    setLegacyOfficeDraft(mapping.legacy_office ?? "");
    setLegacyOfficeReportedDraft(mapping.legacy_office_reported ?? "");
    setSiteDraft(mapping.site ?? "");
    setLegacyLocationReportedDraft(mapping.legacy_location_reported ?? "");
    setMappingPerimeterDraft(mapping.mapping_perimeter ?? "");
    setXeroInclusionDateDraft(mapping.xero_inclusion_date);
  };

  return (
    <TableRow>
      <TableCell className="font-medium text-foreground">
        <span className="flex items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
          {mapping.legacy_office_fetched}
        </span>
      </TableCell>
      <TableCell>
        <Input
          value={legacyOfficeDraft}
          onChange={(e) => setLegacyOfficeDraft(e.target.value)}
          disabled={save.isPending}
          maxLength={200}
          className="h-8"
          placeholder="Add a legacy office"
          aria-label={`Legacy office for ${mapping.legacy_office_fetched}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={legacyOfficeReportedDraft}
          onChange={(e) => setLegacyOfficeReportedDraft(e.target.value)}
          disabled={save.isPending}
          maxLength={200}
          className="h-8"
          placeholder="Add a reported office"
          aria-label={`Legacy office reported for ${mapping.legacy_office_fetched}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={siteDraft}
          onChange={(e) => setSiteDraft(e.target.value)}
          disabled={save.isPending}
          maxLength={200}
          className="h-8"
          placeholder="Add a site"
          aria-label={`Site for ${mapping.legacy_office_fetched}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={legacyLocationReportedDraft}
          onChange={(e) => setLegacyLocationReportedDraft(e.target.value)}
          disabled={save.isPending}
          maxLength={200}
          className="h-8"
          placeholder="Add a reported location"
          aria-label={`Legacy location reported for ${mapping.legacy_office_fetched}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={mappingPerimeterDraft}
          onChange={(e) => setMappingPerimeterDraft(e.target.value)}
          disabled={save.isPending}
          maxLength={200}
          className="h-8"
          placeholder="Add a mapping perimeter"
          aria-label={`Mapping perimeter for ${mapping.legacy_office_fetched}`}
        />
      </TableCell>
      <TableCell>
        <XeroRetrievalMonthCell
          draft={xeroInclusionDateDraft}
          tenantName={mapping.legacy_office_fetched}
          isPending={save.isPending}
          onDraftChange={setXeroInclusionDateDraft}
        />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatUpdatedAt(mapping.updated_at)}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-8"
            onClick={onSave}
            disabled={!isDirty || save.isPending || !canSave}
            title={canSave ? undefined : "This row has no row_id yet and cannot be saved."}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={onCancel}
            disabled={!isDirty || save.isPending}
          >
            Cancel
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
